import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useMemo } from 'react';
import type {
  CustomHeaderRendererProps,
  GetIntervalProps,
  GetIntervalPropsParams,
} from './react-calendar-timeline';
import type { Moment } from 'moment';
import moment from 'moment';
import { useTheme } from 'styled-components';
import type { Feature, Polygon } from '@turf/helpers';
import { point } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

import {
  assertDefined,
  groupBySequence,
  isDefined,
  mapKeyBy,
} from '../../lib/util';
import { getEventLocation } from '../core/Core';
import { getVesselCapacities } from '../core/Vessel';
import type { MassVolumeSize } from '../core/Capacity';
import { midPoint, sumMassVolumeSize } from '../core/Capacity';
import type { Vessel } from '../core/Vessel';
import type { Events } from '../core/TimeData';
import type { RegionPolygons } from '../GoogleMaps';
import { useRegionPolygons } from '../GoogleMaps';
import { MultiKey } from '../core/MultiKey';
import { ArrowDownIcon, ArrowUpIcon } from '../Icons';
import { Text } from '../ui/index';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { EventTimeline } from '../core/Timelines';
import type { Region_AdminFragment } from '../../graphql';
import { uniqBy } from 'lodash';
import { formatNumber } from '../../lib/format';
import { atom, useAtom } from 'jotai';
import theme from '../theme';
import { isInquiry } from '../voyageCharter/orders';
import { useRegionToolAmountsMTSVisibleState } from '../../state/planning/options';
import { useRegionToolAmountsCBMVisibleState } from '../../state/planning/options';
import { DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import type { ScenarioTimelineItem } from './types';
import { ItemType } from './types';
import {
  RegionRow,
  RegionControlRow,
  DayGrid,
  DayGridDay,
  DayGridText,
  RegionToggle,
  RegionHeader,
  Interval,
} from './Regions.styled';

const highlightedRegionsAtom = atom<{
  startRegion: null | string;
  endRegion: null | string;
}>({ startRegion: null, endRegion: null });

export function useRegionToolHighlightedRegion() {
  return useAtom(highlightedRegionsAtom);
}

export type RegionBalanceFilter = Array<{
  region: string;
  toRegion?: string;
  startTime: Moment;
  endTime: Moment;
  key: string | number;
}>;

type CapacityData = {
  /** Available capacity on vessels in open position */
  openVesselCapacity: MassVolumeSize;
  openVesselCount: number;
  /** Available capacity on vessels already assigned from current region */
  remainingVesselCapacity: MassVolumeSize;
  remainingVesselCount: number;
  totalVesselCapacity: MassVolumeSize;
  totalOpenCargo: MassVolumeSize;
  openOrdersCount: number;
};
type IntervalData = CapacityData & {
  intervalDataByToRegion: Map<string, CapacityData>;
};

const getCapacityNumbers = ({
  amountsMTSVisible,
  amountsCBMVisible,
  data: {
    openVesselCapacity,
    remainingVesselCapacity,
    totalVesselCapacity,
    totalOpenCargo,
    openVesselCount,
    remainingVesselCount,
    openOrdersCount,
  },
}: {
  amountsMTSVisible: boolean;
  amountsCBMVisible: boolean;
  data: CapacityData;
}): { title: string; capacityNumbers: JSX.Element } => {
  const tonnage =
    amountsCBMVisible || amountsMTSVisible
      ? sumMassVolumeSize([openVesselCapacity, remainingVesselCapacity])
      : openVesselCapacity;
  const massPct =
    totalVesselCapacity.mass &&
    Math.round((tonnage.mass / totalVesselCapacity.mass) * 100);
  const volumePct =
    totalVesselCapacity.volume &&
    Math.round((tonnage.volume / totalVesselCapacity.volume) * 100);
  const totalVesselCount = openVesselCount + remainingVesselCount;
  const tonnageMts = tonnage.mass
    ? `${formatNumber(tonnage.mass, undefined, 2)} mts`
    : '0';
  const tonnageCbm = tonnage.volume
    ? `${formatNumber(tonnage.volume, undefined, 2)} cbm`
    : '0';
  const cargoMts = totalOpenCargo.mass
    ? `${formatNumber(totalOpenCargo.mass, undefined, 2)} mts`
    : '0';
  const cargoCbm = totalOpenCargo.volume
    ? `${formatNumber(totalOpenCargo.volume, undefined, 2)} cbm`
    : '0';

  const title =
    amountsCBMVisible || amountsMTSVisible
      ? `Tonnage: ${totalVesselCount} vessels (${tonnageMts}/${tonnageCbm}) \nCargo: ${openOrdersCount} orders: (${cargoMts}/${cargoCbm})`
      : `${openOrdersCount} orders (${cargoMts}/${cargoCbm}) \n${openVesselCount} vessels (${tonnageMts}/${tonnageCbm})`;

  const capacityNumbers =
    amountsMTSVisible && amountsCBMVisible ? (
      <>
        <DayGridText dimmed={tonnageMts === '0' && cargoMts === '0'}>
          T: {tonnageMts} ({massPct}%) ({totalVesselCount})
          <br />
          C: {cargoMts} ({openOrdersCount})
        </DayGridText>
        <DayGridText dimmed={tonnageCbm === '0' && cargoCbm === '0'}>
          T: {tonnageCbm} ({volumePct}%) ({totalVesselCount})
          <br />
          C: {cargoCbm} ({openOrdersCount})
        </DayGridText>
      </>
    ) : amountsCBMVisible ? (
      <DayGridText dimmed={tonnageCbm === '0' && cargoCbm === '0'}>
        T: {tonnageCbm} ({volumePct}%) ({totalVesselCount})
        <br />
        C: {cargoCbm} ({openOrdersCount})
      </DayGridText>
    ) : amountsMTSVisible ? (
      <DayGridText dimmed={tonnageMts === '0' && cargoMts === '0'}>
        T: {tonnageMts} ({massPct}%) ({totalVesselCount})
        <br />
        C: {cargoMts} ({openOrdersCount})
      </DayGridText>
    ) : (
      <>
        <DayGridText />
        <DayGridText
          dimmed={openOrdersCount === 0}
          style={{ textAlign: 'center' }}
        >
          {openOrdersCount}
        </DayGridText>
        <DayGridText
          dimmed={openVesselCount === 0}
          style={{ textAlign: 'center' }}
        >
          {openVesselCount}
        </DayGridText>
        <DayGridText />
      </>
    );

  return { title, capacityNumbers };
};

const Region = ({
  region,
  regions,
  regionOpenData,
  intervals,
  unit,
  intervalData,
  selectedRegion,
  getIntervalProps,
  regionBalanceFilter,
  setRegionBalanceFilter,
}: {
  region: { properties: { name: string } };
  regions: { properties: { name: string } }[];
  regionOpenData: RegionOpenData;
  intervals: {
    startTime: Moment;
    endTime: Moment;
    labelWidth: number;
  }[];
  unit: HeaderContext['unit'];
  intervalData: IntervalData[];
  getIntervalProps: (props?: GetIntervalPropsParams) => GetIntervalProps;
  regionBalanceFilter: RegionBalanceFilter;
  setRegionBalanceFilter: (newFilter: RegionBalanceFilter) => void;
  selectedRegion: string | null;
}) => {
  const [, setHighlightedRegion] = useRegionToolHighlightedRegion();
  const [amountsMTSVisible] = useRegionToolAmountsMTSVisibleState();
  const [amountsCBMVisible] = useRegionToolAmountsCBMVisibleState();

  const name = region.properties.name;

  if (!regionOpenData.regionPairsWithOpenData.has(name)) {
    return null;
  }

  const indicators = intervals.map((interval, i) => {
    const day = interval.startTime.day();
    const weekend = unit === 'day' && (day === 0 || day === 6);
    const intervalProps = getIntervalProps({
      interval,
    });
    const key = intervalProps.key;
    const selected = regionBalanceFilter.some(
      (regionFilter) =>
        regionFilter.region === name && regionFilter.key === key,
    );

    const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const newFilter: RegionBalanceFilter[number] = {
        region: name,
        key,
        startTime: moment(interval.startTime).subtract(3, 'days'),
        endTime: moment(interval.endTime).add(3, 'days'),
      };

      if (e.ctrlKey || e.metaKey) {
        // Multi-select
        if (selected) {
          setRegionBalanceFilter(
            regionBalanceFilter.filter(
              (regionFilter) =>
                regionFilter.region !== name || regionFilter.key !== key,
            ),
          );
        } else {
          setRegionBalanceFilter([...regionBalanceFilter, newFilter]);
        }
      } else {
        // Single-select
        if (selected) {
          setRegionBalanceFilter([]);
        } else {
          setRegionBalanceFilter([newFilter]);
        }
      }
    };

    const { title, capacityNumbers } = getCapacityNumbers({
      amountsMTSVisible,
      amountsCBMVisible,
      data: intervalData[i],
    });

    return (
      // eslint-disable-next-line react/jsx-key
      <Interval title={title} {...intervalProps}>
        {interval.labelWidth > 10 ? (
          <DayGridDay
            style={{
              backgroundColor: selected
                ? '#ffffff'
                : selectedRegion === name
                  ? '#c3cedd'
                  : weekend
                    ? '#a7b7cc'
                    : '#e8ebef',
              overflow: 'hidden',
            }}
            onClick={onClick}
          >
            {capacityNumbers}
          </DayGridDay>
        ) : null}
      </Interval>
    );
  });

  return (
    <>
      <RegionRow
        key={name}
        onMouseEnter={() =>
          setHighlightedRegion({ startRegion: name, endRegion: null })
        }
      >
        <DayGrid>{indicators}</DayGrid>
      </RegionRow>
      {selectedRegion === name && (
        <>
          {regions.map((region, i) => {
            const toName = region.properties.name;
            if (
              !regionOpenData.regionPairsWithOpenData.get(name)?.has(toName)
            ) {
              return null;
            }
            const indicators = intervals.map((interval, i) => {
              const { intervalDataByToRegion } = intervalData[i];
              const capacityData = intervalDataByToRegion.get(
                region.properties.name,
              );
              if (!capacityData) return null;

              const day = interval.startTime.day();
              const weekend = (unit === 'day' && day === 0) || day === 6;
              const intervalProps = getIntervalProps({
                interval,
              });
              const key = intervalProps.key;
              const selected = regionBalanceFilter.some(
                (regionFilter) =>
                  regionFilter.region === selectedRegion &&
                  regionFilter.key === key &&
                  (regionFilter.toRegion ?? toName) === toName,
              );

              const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
                const newFilter: RegionBalanceFilter[number] = {
                  region: selectedRegion,
                  toRegion: toName,
                  key,
                  startTime: moment(interval.startTime).subtract(3, 'days'),
                  endTime: moment(interval.endTime).add(3, 'days'),
                };

                if (e.ctrlKey || e.metaKey) {
                  // Multi-select
                  if (selected) {
                    setRegionBalanceFilter(
                      regionBalanceFilter.filter(
                        (regionFilter) =>
                          regionFilter.region !== toName ||
                          regionFilter.key !== key,
                      ),
                    );
                  } else {
                    setRegionBalanceFilter([...regionBalanceFilter, newFilter]);
                  }
                } else {
                  // Single-select
                  if (selected) {
                    setRegionBalanceFilter([]);
                  } else {
                    setRegionBalanceFilter([newFilter]);
                  }
                }
              };

              const { title, capacityNumbers } = getCapacityNumbers({
                amountsMTSVisible,
                amountsCBMVisible: amountsCBMVisible,
                data: capacityData,
              });

              return (
                // eslint-disable-next-line react/jsx-key
                <Interval title={title} {...intervalProps}>
                  {interval.labelWidth > 10 ? (
                    <DayGridDay
                      style={{
                        backgroundColor: selected
                          ? '#ffffff'
                          : selectedRegion === name
                            ? '#c3cedd'
                            : weekend
                              ? '#a7b7cc'
                              : '#d5dce5',
                      }}
                      onClick={onClick}
                    >
                      {capacityNumbers}{' '}
                    </DayGridDay>
                  ) : null}
                </Interval>
              );
            });
            return (
              <RegionRow
                key={i}
                style={{
                  backgroundColor:
                    selectedRegion === name ? '#c3cedd' : undefined,
                }}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  setHighlightedRegion({
                    startRegion: name,
                    endRegion: toName,
                  });
                }}
              >
                <DayGrid>{indicators}</DayGrid>
              </RegionRow>
            );
          })}
        </>
      )}
    </>
  );
};

function getOpenPositionData(items: ScenarioTimelineItem[]) {
  const scheduledOrderIds = new Set<number>();
  const openTimes = new Map<number, number>();
  const openPorts = new Map<number, number>();

  for (const item of items) {
    if (
      item.type === ItemType.SUGGESTION ||
      item.type === ItemType.RESERVATION
    ) {
      continue;
    }

    if (
      item.type === ItemType.SPOT ||
      item.type === ItemType.COA ||
      item.type === ItemType.SCHEDULED_ORDER
    ) {
      for (const order of item.orders) {
        scheduledOrderIds.add(order.id);
      }
    }

    const timeline = 'groupObj' in item ? item.groupObj?.timeline : null;
    const timelineType = timeline?.type;
    const vesselId = timeline?.vesselId;
    const vessel = timeline?.vessel;
    if (timelineType === 'tc' && vesselId && !vessel?.externalFleet) {
      let maxEnd = undefined;
      let lastEvent: Events | undefined = undefined;

      if (
        item.type === ItemType.EVENT ||
        item.type === ItemType.OFFHIRE ||
        item.type === ItemType.TC_IN
      ) {
        lastEvent = item.timeData.event;
        maxEnd = item.timeData.end;
      } else if (
        item.type !== ItemType.OPEN &&
        item.type !== ItemType.COMPLETED
      ) {
        const td = item.events[item.events.length - 1];
        lastEvent = td?.event;
        maxEnd = td?.end;
      }

      if (lastEvent && maxEnd) {
        const shipOpen = openTimes.get(vesselId);
        if (!shipOpen || maxEnd > shipOpen) {
          openTimes.set(vesselId, maxEnd);
          const locationId =
            'locationId' in lastEvent
              ? lastEvent.locationId
              : lastEvent.locationBId;
          openPorts.set(vesselId, locationId);
        }
      }
    }
  }

  return {
    /** Per vesselId the last port it visits */
    openPorts,
    /** Per vesselId the end of the last event in its timeline */
    openTimes,
    scheduledOrderIds,
  };
}

interface HeaderContext {
  intervals: { startTime: Moment; endTime: Moment; labelWidth: number }[];
  unit: string;
}

function calculateOpenOrders<T extends ScenarioOrder>(
  openOrders: T[],
  endTime: number,
  startTime: number,
) {
  return openOrders.filter(
    (order) =>
      (!order.loadingWindow.start || endTime > order.loadingWindow.start) &&
      (!order.loadingWindow.end || startTime < order.loadingWindow.end),
  );
}

const defaultValues: IntervalData = {
  openVesselCapacity: { mass: 0, volume: 0 },
  openVesselCount: 0,
  remainingVesselCapacity: { mass: 0, volume: 0 },
  remainingVesselCount: 0,
  totalVesselCapacity: { mass: 0, volume: 0 },
  totalOpenCargo: { mass: 0, volume: 0 },
  openOrdersCount: 0,
  intervalDataByToRegion: new Map(),
};

export type RegionHeaderData = {
  regionPolygons: RegionPolygons | undefined;
  regionBalanceFilter: RegionBalanceFilter;
  setRegionBalanceFilter: Dispatch<SetStateAction<RegionBalanceFilter>>;
  showRegions: boolean;
  setShowRegions: (showRegions: boolean) => void;
  selectedRegion: string | null;
  regionOpenData: RegionOpenData;
};

type RegionHeaderProps = CustomHeaderRendererProps<RegionHeaderData> & {
  headerContext: HeaderContext;
};

const supportedUnits = ['day', 'isoWeek', 'month'] as const;
type SupportedIntervalUnit = (typeof supportedUnits)[number];
function isSupportedInterval(unit: string): unit is SupportedIntervalUnit {
  return supportedUnits.includes(unit as SupportedIntervalUnit);
}

export type WithRegionNames<T> = T & {
  fromRegion?: string | null;
  toRegion?: string | null;
};

type Interval = { startTime: Moment; endTime: Moment };

type RegionOrder = WithRegionNames<ScenarioOrder>;

type RegionChange = {
  fromRegion: string;
  toRegion: string | null;
  isReservation: boolean;
  vessel: Vessel;
  availableMassCapacity: number | null;
  availableVolumeCapacity: number | null;
  start: number;
  end: number | null;
};

/** Open vessel capacities and order sizes for the given region interval */
const calculateCapacityData = (
  regionChanges: RegionChange[],
  openPositions: RegionChange[],
  openOrders: RegionOrder[],
): CapacityData => {
  const openVesselCapacity: MassVolumeSize = sumMassVolumeSize(
    openPositions.map(
      (c): MassVolumeSize => ({
        mass: c.availableMassCapacity ?? 0,
        volume: c.availableVolumeCapacity ?? 0,
      }),
    ),
  );

  const remainingVesselCapacity: MassVolumeSize = sumMassVolumeSize(
    regionChanges.map(
      (c): MassVolumeSize => ({
        mass: c.availableMassCapacity ?? 0,
        volume: c.availableVolumeCapacity ?? 0,
      }),
    ),
  );

  const orderSizes: MassVolumeSize[] = openOrders.map((o) => {
    return {
      mass: midPoint(o.cargoRanges.mass),
      volume: midPoint(o.cargoRanges.volume),
    };
  });

  const vessels = uniqBy(
    [...regionChanges, ...openPositions].map((c) => c.vessel),
    (v) => v.id,
  );
  const vesselCapacities = vessels.map((vessel): MassVolumeSize => {
    const capacity = getVesselCapacities(vessel, null);
    return {
      mass: capacity.massCapacity ?? 0,
      volume: capacity.volumeCapacity ?? 0,
    };
  });
  const totalVesselCapacity = sumMassVolumeSize(vesselCapacities);

  const totalOpenCargo: MassVolumeSize = sumMassVolumeSize(orderSizes);

  return {
    openVesselCapacity,
    openVesselCount: openPositions.length,
    remainingVesselCapacity,
    remainingVesselCount: regionChanges.length,
    totalVesselCapacity,
    totalOpenCargo,
    openOrdersCount: openOrders.length,
  };
};

export type RegionOpenData = ReturnType<typeof calculateRegionOpenData>;
export const calculateRegionOpenData = ({
  regions,
  orders,
  items,
  vessels,
  timelines,
  locations,
  regionsByLocation,
}: {
  regions: Region_AdminFragment[];
  orders: RegionOrder[];
  items: ScenarioTimelineItem[];
  vessels: Map<number, Vessel>;
  timelines: EventTimeline[];
  locations: Map<number, ScenarioLocation>;
  regionsByLocation: DefaultMap<LocationData | undefined | null, string | null>;
}) => {
  const regionsById: Map<number, Region_AdminFragment> = mapKeyBy(
    regions,
    (r) => r.id,
  );
  const { scheduledOrderIds, openTimes } = getOpenPositionData(items);

  /** Also filters out inquiries, which we don't want to see in the region tool */
  const openOrdersByRegion = new DefaultMap<string, RegionOrder[]>(() => []);

  orders.forEach((order) => {
    if (!scheduledOrderIds.has(order.id) && !isInquiry(order)) {
      openOrdersByRegion.get(order.fromRegion ?? 'Others').push(order);
    }
  });

  // Calculate region changes and open positions

  const vesselRegionChanges = new MultiKey<
    RegionChange[],
    [fromRegion: string, toRegion: string, vesselId: number]
  >(3);

  const regionOpenPositions = new MultiKey<
    RegionChange,
    [
      fromRegion: string,
      toRegion: string | null,
      /** Indexing by vesselId not necessary, but ensures inserts never add duplicates */
      vesselId: number,
    ]
  >(3);

  for (const item of items) {
    if (item.type === ItemType.RESERVATION) {
      const { vesselReservation } = item;
      const vessel = vessels.get(vesselReservation.vesselId);

      if (!vessel || item.end_time <= (openTimes.get(vessel.id) ?? 0)) {
        // Only consider reservations when the reservation is still at the end of timeline
        continue;
      }
      let reservationStartRegion: string | undefined | null;
      let reservationEndRegion: string | undefined | null;

      if (isDefined(vesselReservation.startPortId)) {
        const location = locations.get(vesselReservation.startPortId);
        reservationStartRegion = regionsByLocation.get(location);
      } else if (isDefined(vesselReservation.startRegionId)) {
        reservationStartRegion = regionsById.get(
          vesselReservation.startRegionId,
        )?.name;
      }
      if (isDefined(vesselReservation.endPortId)) {
        const location = locations.get(vesselReservation.endPortId);
        reservationEndRegion = regionsByLocation.get(location);
      } else if (isDefined(vesselReservation.endRegionId)) {
        reservationEndRegion = regionsById.get(
          vesselReservation.endRegionId,
        )?.name;
      }

      if (reservationStartRegion && reservationEndRegion) {
        const { massCapacity, volumeCapacity } = getVesselCapacities(vessel, 0);
        const regionChange: RegionChange = {
          fromRegion: reservationStartRegion,
          toRegion: reservationEndRegion,
          isReservation: true,
          vessel,
          availableMassCapacity: massCapacity,
          availableVolumeCapacity: volumeCapacity,
          start: item.start_time,
          end: item.end_time,
        };

        regionOpenPositions.update(
          (oldValue) =>
            oldValue && oldValue.start > regionChange.start
              ? oldValue
              : regionChange,
          reservationStartRegion,
          reservationEndRegion,
          vessel.id,
        );

        const openPosition: RegionChange = {
          fromRegion: reservationEndRegion,
          toRegion: null,
          isReservation: true,
          vessel,
          availableMassCapacity: massCapacity,
          availableVolumeCapacity: volumeCapacity,
          start: item.end_time,
          end: item.end_time,
        };

        regionOpenPositions.update(
          (oldValue) =>
            oldValue && oldValue.start > openPosition.start
              ? oldValue
              : openPosition,
          reservationEndRegion,
          null,
          vessel.id,
        );
      }
    }
  }

  for (const timeline of timelines) {
    if (timeline.type !== 'tc' || timeline.vessel.externalFleet) continue;
    const events = timeline.voyageGroups.flatMap((vg) => vg.events);
    const eventsPerRegion = groupBySequence(events, (td) => {
      const location = getEventLocation(td.event, locations);
      return regionsByLocation.get(location);
    });

    const { massCapacity, volumeCapacity } = getVesselCapacities(
      timeline.vessel,
      null,
    );

    for (let i = 0; i < eventsPerRegion.length; ++i) {
      const regionEvents = eventsPerRegion[i];

      const lastRegionEvent = regionEvents.at(-1);
      assertDefined(
        lastRegionEvent,
        `Expected events array to only exists if it contains events`,
      );
      const leaveRegionAt = lastRegionEvent.end;

      const firstRegionEvent = regionEvents[0];
      const location = getEventLocation(firstRegionEvent.event, locations);
      if (!location) continue;
      const region = regionsByLocation.get(location);
      if (!region) continue;

      const nextRegionEvents = eventsPerRegion.at(i + 1);
      const nextLocation =
        nextRegionEvents &&
        getEventLocation(nextRegionEvents[0].event, locations);
      const nextRegion =
        (nextLocation && regionsByLocation.get(nextLocation)) ?? null;

      const regionChange: RegionChange = {
        fromRegion: region,
        toRegion: nextRegion,
        isReservation: false,
        vessel: timeline.vessel,
        availableMassCapacity:
          massCapacity === null ? 0 : lastRegionEvent.availableMassCapacity,
        availableVolumeCapacity:
          volumeCapacity === null ? 0 : lastRegionEvent.availableVolumeCapacity,
        start: leaveRegionAt,
        end: nextRegionEvents?.[0].start ?? null,
      };

      if (nextRegion === null) {
        regionOpenPositions.update(
          (oldValue) =>
            oldValue && oldValue.start > regionChange.start
              ? oldValue
              : regionChange,
          region,
          nextRegion,
          timeline.vessel.id,
        );
      } else {
        vesselRegionChanges.update(
          (value = []) => {
            value.push(regionChange);
            return value;
          },
          region,
          nextRegion,
          timeline.vesselId,
        );
      }
    }
  }

  const regionPairsWithOpenData = new DefaultMap<string, Set<string>>(
    () => new Set(),
  );
  [
    ...regionOpenPositions.getSubTreeValues().flat(),
    ...vesselRegionChanges.getSubTreeValues().flat(),
  ].forEach(({ fromRegion, toRegion }) => {
    if (toRegion === null) return;
    regionPairsWithOpenData.update(fromRegion, (value = new Set()) =>
      value.add(toRegion),
    );
  });

  const topLevelRegionsWithOpenData = new Set(
    regionOpenPositions
      .getSubTreeValues()
      .flat()
      .filter((rc) => rc.toRegion === null)
      .map((rc) => rc.fromRegion),
  );

  return {
    vesselRegionChanges,
    regionOpenPositions,
    openOrdersByRegion,
    regionPairsWithOpenData,
    topLevelRegionsWithOpenData,
  };
};

/*
  Number of vessels open at noon on that day in that region = if vessel is either idle/waiting in that region or if last discharge on vessel's schedule has ended in this region before noon that day
  Number of open orders with the loading window open at noon on that day, orders without loading window are always counted in
*/
export const RegionHeaderIntervals = ({
  headerContext: { intervals, unit },
  getRootProps,
  getIntervalProps,
  data,
}: RegionHeaderProps): JSX.Element => {
  const theme = useTheme();
  if (!isSupportedInterval(unit)) {
    throw new Error(
      `Interval unit ${unit} not supported by RegionHeaderIntervals`,
    );
  }
  const [, setHighlightedRegion] = useRegionToolHighlightedRegion();

  const {
    regionPolygons = [],
    regionBalanceFilter,
    setRegionBalanceFilter,
    setShowRegions,
    selectedRegion,
    regionOpenData,
  } = data;

  const regions = useMemo(() => {
    const others = { properties: { name: 'Others' } };
    return [...regionPolygons, others];
  }, [regionPolygons]);

  const findIntervalForTimeStamp = useCallback(
    (timestamp: number): Interval | undefined => {
      return intervals.find(
        (interval) =>
          interval.startTime.valueOf() <= timestamp &&
          timestamp < interval.endTime.valueOf(),
      );
    },
    [intervals],
  );

  const vesselRegionChangesByInterval = useMemo(() => {
    const vesselRegionChangesByInterval = new MultiKey<
      RegionChange,
      [
        fromRegion: string,
        interval: Interval,
        toRegion: string | null,
        /** Indexing by vesselId not necessary, but ensures inserts never add duplicates */
        vesselId: number,
      ]
    >(4);

    for (const regionChange of regionOpenData.vesselRegionChanges
      .getSubTreeValues()
      .flat()) {
      const interval = findIntervalForTimeStamp(regionChange.start);
      if (!interval) continue;
      vesselRegionChangesByInterval.set(
        regionChange,
        regionChange.fromRegion,
        interval,
        regionChange.toRegion,
        regionChange.vessel.id,
      );
    }

    return vesselRegionChangesByInterval;
  }, [regionOpenData.vesselRegionChanges, findIntervalForTimeStamp]);

  /** Computed data for intervals for which we have data available */
  const cachedIntervalData = useMemo(() => {
    const result = new MultiKey<
      IntervalData,
      [region: string, interval: Interval]
    >(3);
    for (const interval of intervals) {
      const start = interval.startTime.valueOf();
      const end = interval.endTime.valueOf();

      for (const r of regions) {
        const key = r.properties.name;

        const regionChanges =
          vesselRegionChangesByInterval.getSubTreeValues(key, interval) ?? [];

        /** Actual open positions */
        const openPositions = regionOpenData.regionOpenPositions
          .getSubTreeValues(key, null)
          .filter((c) => c.start < interval.endTime.valueOf());
        const openOrdersForRegion = regionOpenData.openOrdersByRegion.get(key);
        const openOrdersForRegionInterval = calculateOpenOrders(
          openOrdersForRegion,
          end,
          start,
        );

        const openOrdersByToRegion = mapGroupBy(
          openOrdersForRegionInterval,
          (order) => order.toRegion ?? 'Others',
        );

        const intervalDataByToRegion = new Map<string, CapacityData>();
        for (const toRegion of regions) {
          const toRegionChanges =
            vesselRegionChangesByInterval.getSubTreeValues(
              key,
              interval,
              toRegion.properties.name,
            ) ?? [];
          const toRegionOpenPositions = regionOpenData.regionOpenPositions
            .getSubTreeValues(key, toRegion.properties.name)
            .filter(
              (c) =>
                c.start < interval.endTime.valueOf() &&
                (c.end ?? Infinity) > interval.startTime.valueOf(),
            );

          const toRegionOpenOrders =
            openOrdersByToRegion.get(toRegion.properties.name) ?? [];

          const toRegionCapacityData = calculateCapacityData(
            toRegionChanges,
            toRegionOpenPositions,
            toRegionOpenOrders,
          );

          intervalDataByToRegion.set(
            toRegion.properties.name,
            toRegionCapacityData,
          );
        }

        const regionCapacityData = calculateCapacityData(
          regionChanges,
          openPositions,
          openOrdersForRegionInterval,
        );
        result.set(
          {
            ...regionCapacityData,
            intervalDataByToRegion,
          },
          key,
          interval,
        );
      }
    }
    return result;
  }, [
    intervals,
    regions,
    vesselRegionChangesByInterval,
    regionOpenData.regionOpenPositions,
    regionOpenData.openOrdersByRegion,
  ]);

  /** Combined data for all regions and intervals, includes computed intervals and placeholders for missing intervals */
  const intervalData: IntervalData[][] = useMemo(() => {
    return regions.map((r): IntervalData[] => {
      const key = r.properties.name;
      return intervals.map((interval): IntervalData => {
        return cachedIntervalData.get(key, interval) ?? defaultValues;
      });
    });
  }, [regions, intervals, cachedIntervalData]);

  return (
    <div
      {...getRootProps()}
      style={{ zIndex: 200 }}
      onMouseLeave={() =>
        setHighlightedRegion({ startRegion: null, endRegion: null })
      }
    >
      {regions.map((r, i) => (
        <Region
          key={r.properties.name}
          region={r}
          regions={regions}
          regionOpenData={regionOpenData}
          intervals={intervals}
          unit={unit}
          intervalData={intervalData[i]}
          selectedRegion={selectedRegion}
          getIntervalProps={getIntervalProps}
          regionBalanceFilter={regionBalanceFilter}
          setRegionBalanceFilter={setRegionBalanceFilter}
        />
      ))}
      <RegionControlRow
        onClick={() => {
          setShowRegions(false);
        }}
      >
        <RegionToggle>
          Hide regions
          <ArrowUpIcon fillColor={theme.button.default.text} />
        </RegionToggle>
      </RegionControlRow>
    </div>
  );
};

export const RegionHeaderToggle = ({
  getRootProps,
  data,
}: RegionHeaderProps): JSX.Element => {
  const { setShowRegions, regionBalanceFilter } = data;
  const selectedRegions = regionBalanceFilter.map((x) => x.region).join(',');
  const offset = selectedRegions.length;
  const theme = useTheme();
  return (
    <div {...getRootProps()}>
      <RegionControlRow
        onClick={() => {
          setShowRegions(true);
        }}
      >
        <RegionToggle offset={offset}>
          Show regions
          <ArrowDownIcon fillColor={theme.button.primary.text} />
          {!!offset && (
            <Text color="white" fontWeight="bold" isUppercase>
              ({selectedRegions})
            </Text>
          )}
        </RegionToggle>
      </RegionControlRow>
    </div>
  );
};

export function RegionsListHeaderSidebar({
  regions,
  regionOpenData,
  selectedRegion,
  setSelectedRegion,
}: {
  regions: { properties: { name: string } }[];
  regionOpenData: RegionOpenData;
  selectedRegion: string | null;
  setSelectedRegion: React.Dispatch<React.SetStateAction<string | null>>;
}): React.ReactNode {
  const [highlightedRegion] = useRegionToolHighlightedRegion();

  return regions.map((r) => {
    const { name } = r.properties;
    if (
      !(
        regionOpenData.regionPairsWithOpenData.has(name) ||
        regionOpenData.topLevelRegionsWithOpenData.has(name)
      )
    ) {
      return null;
    }
    const selected = selectedRegion === name;
    const onClick = () => {
      setSelectedRegion(selected ? null : name);
    };
    if (!selected) {
      return (
        <RegionRow key={name} onClick={onClick}>
          <RegionHeader
            style={{
              backgroundColor:
                highlightedRegion.startRegion === name
                  ? theme.colors.neutral.gray_150
                  : undefined,
            }}
          >
            {name}{' '}
            {regionOpenData.regionPairsWithOpenData.get(name).size > 0 && (
              <ArrowDownIcon fillColor={theme.colors.text} />
            )}
          </RegionHeader>
        </RegionRow>
      );
    } else {
      return (
        <>
          <RegionRow key={name} onClick={onClick}>
            <RegionHeader
              style={{
                backgroundColor:
                  highlightedRegion.startRegion === name
                    ? theme.colors.neutral.gray_150
                    : undefined,
              }}
            >
              {name} <ArrowUpIcon fillColor={theme.colors.text} />
            </RegionHeader>
          </RegionRow>
          {regions.map((r) => {
            const toName = r.properties.name;
            if (
              !regionOpenData.regionPairsWithOpenData.get(name)?.has(toName)
            ) {
              return null;
            }

            return (
              <RegionRow key={`${name}-${toName}`}>
                <RegionHeader
                  style={{
                    paddingLeft: '1.5em',
                    backgroundColor:
                      highlightedRegion.endRegion === r.properties.name
                        ? theme.colors.neutral.gray_150
                        : undefined,
                  }}
                >
                  {' >'} {r.properties.name}
                </RegionHeader>
              </RegionRow>
            );
          })}
        </>
      );
    }
  });
}

export type LocationData =
  | {
      latitude?: string | number | null;
      longitude?: string | number | null;
    }
  | undefined
  | null;

export function getRegionForLocation(
  location: LocationData,
  regionPolygons: Feature<Polygon, { name: string }>[] | undefined,
) {
  if (!location) return undefined;

  const latitude = Number(location.latitude);
  if (!isFinite(latitude)) return undefined;

  const longitude = Number(location.longitude);
  if (!isFinite(longitude)) return undefined;

  const pt = point([latitude, longitude]);

  const region = regionPolygons?.find((polygon) =>
    booleanPointInPolygon(pt, polygon),
  );

  return region?.properties.name;
}

export function useRegions(): {
  regions: DefaultMap<LocationData | undefined | null, string | null>;
  regionPolygons: Feature<Polygon, { name: string }>[] | undefined;
} {
  const regionPolygons = useRegionPolygons();
  const regions = useMemo(
    () =>
      new DefaultMap<LocationData | undefined | null, string | null>(
        (location: LocationData) =>
          (location && getRegionForLocation(location, regionPolygons)) ?? null,
      ),
    [regionPolygons],
  );
  return { regionPolygons, regions };
}
